import { dashboardStats } from '@/mocks/homeData';

export default function HeroSection() {
  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(val);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-background-50"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50/50"></div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 text-center lg:text-left w-full">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-50 text-red-700 border border-red-100 rounded-full text-xs font-bold mb-6">
              <i className="ri-alarm-warning-line"></i>
              <span>Sadece bir tahsilat kaçağını önlese bile kendi maliyetini çıkaran sistem!</span>
            </div>

            <h1 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-foreground-950 leading-tight tracking-tight mb-6">
              Kurban Bayramı&apos;nda nakit akışınızı şansa bırakmayın:{' '}
              <span className="text-primary-500">sıfır hatalı kurban ve çiftlik yönetim sistemi.</span>
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto lg:mx-0 mb-8">
              100 büyükbaşlık bir operasyonda dönen milyonlarca liralık nakit trafiğini Excel tablolarına ve defter köşelerine emanet etmeyin. Hisse yönetiminden dijital kurban kesim sıramatiğine, müşteri portalından anlık havale eşleştirmeye kadar tüm sürecinizi mobil uyumlu bulut tabanlı yazılımla, cep telefonunuzdan yönetin.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8">
              <a
                href="#demo"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 text-sm md:text-base font-semibold rounded-md hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Sistemin İçini Keşfet (Demo Talep Et)
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <a
                href="#portal"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#portal')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-foreground-200 text-foreground-700 text-sm md:text-base font-semibold rounded-md hover:border-primary-400 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Müşteri Portalını İncele
                <i className="ri-smartphone-line text-lg"></i>
              </a>
            </div>

            <div className="flex items-center justify-center lg:justify-start gap-2 text-sm text-foreground-500">
              <i className="ri-information-line"></i>
              <span>
                Kurulum 70.000 TL + KDV, aylık 7.000 TL + KDV. PostgreSQL altyapısı, rol bazlı yetki ve uygulama indirmeden cepten erişim tek ürün modelindedir.
              </span>
            </div>
          </div>

          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="bg-background-50 border border-background-200/70 rounded-xl shadow-sm overflow-hidden">
              <div className="bg-primary-500 px-5 py-3 flex items-center justify-between">
                <span className="text-background-50 text-sm font-semibold font-heading">
                  CiftlikWeb Yönetim Paneli
                </span>
                <span className="text-background-50/80 text-xs">Bugün, 09:42</span>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4">
                <div className="bg-accent-50 rounded-lg p-4 border border-accent-100/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-md">
                      <i className="ri-knife-line text-accent-600 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-600">Bugünkü Kesim Planı</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950">{dashboardStats.todaySlaughter}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">planlanan kesim</p>
                </div>

                <div className="bg-primary-50 rounded-lg p-4 border border-primary-100/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-md">
                      <i className="ri-pie-chart-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-600">Kurban Hisse Doluluk</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950">%{dashboardStats.shareOccupancy}</p>
                  <div className="mt-2 w-full bg-background-200 rounded-full h-1.5">
                    <div
                      className="bg-primary-500 h-1.5 rounded-full"
                      style={{ width: `${dashboardStats.shareOccupancy}%` }}
                    ></div>
                  </div>
                </div>

                <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-100/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-secondary-100 rounded-md">
                      <i className="ri-exchange-dollar-line text-secondary-600 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-600">Bekleyen Havaleler</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950">{dashboardStats.pendingTransfers}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">eşleşmeyi bekliyor</p>
                </div>

                <div className="bg-accent-50 rounded-lg p-4 border border-accent-100/50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-md">
                      <i className="ri-truck-line text-accent-600 text-sm"></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-600">Taşıma / Teslimat</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground-950">{dashboardStats.transportPending}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">bekleyen teslimat</p>
                </div>
              </div>

              <div className="px-5 pb-5 flex gap-4">
                <div className="flex-1 bg-secondary-50 rounded-lg p-3 border border-secondary-100/50">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-money-dollar-circle-line text-secondary-600 text-sm"></i>
                    <span className="text-xs font-medium text-foreground-600">Açık Tahsilatlar</span>
                  </div>
                  <p className="text-xl font-bold text-foreground-950">{formatCurrency(dashboardStats.openCollections)}</p>
                </div>
                <div className="flex-1 bg-primary-50 rounded-lg p-3 border border-primary-100/50">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-stack-line text-primary-600 text-sm"></i>
                    <span className="text-xs font-medium text-foreground-600">Canlı Stok</span>
                  </div>
                  <p className="text-xl font-bold text-foreground-950">{dashboardStats.activeAnimals}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
